/**
 * Character-level diff shared by the XLSX export and the on-screen tables, so
 * both highlight exactly the same characters.
 */

/** One stretch of a value that is either shared with, or absent from, the other
 * side of a was/new pair — `diff` chars are the ones that changed. */
export interface Seg {
  text: string;
  diff: boolean;
}

/**
 * Character-level LCS diff of two strings. Returns the run breakdown for each
 * side: on `was`, the removed characters are flagged; on `now`, the inserted
 * ones. Common characters stay unflagged on both. Values here are short
 * (barcodes, BOMs, a description at most), so the O(n·m) table is cheap.
 */
export function diffChars(a: string, b: string): { was: Seg[]; now: Seg[] } {
  const n = a.length;
  const m = b.length;
  // dp[i][j] = LCS length of a[i:] and b[j:].
  const dp: number[][] = [];
  for (let i = 0; i <= n; i++) dp.push(new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    const cur = dp[i]!;
    const nxt = dp[i + 1]!;
    for (let j = m - 1; j >= 0; j--) {
      cur[j] = a[i] === b[j] ? nxt[j + 1]! + 1 : Math.max(nxt[j]!, cur[j + 1]!);
    }
  }
  const was: Seg[] = [];
  const now: Seg[] = [];
  const push = (arr: Seg[], ch: string, diff: boolean): void => {
    const last = arr[arr.length - 1];
    if (last && last.diff === diff) last.text += ch;
    else arr.push({ text: ch, diff });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push(was, a[i]!, false);
      push(now, b[j]!, false);
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      push(was, a[i]!, true); // a[i] not in the LCS from here → removed
      i++;
    } else {
      push(now, b[j]!, true); // b[j] not in the LCS from here → inserted
      j++;
    }
  }
  while (i < n) push(was, a[i++]!, true);
  while (j < m) push(now, b[j++]!, true);
  return { was, now };
}
